import type { QuickBooksArTrendAnalysis } from '../../data/ServerApiClient'

type ArSourceRow = Record<string, any>

type ArPeriodGrain = 'month' | 'quarter' | 'year' | 'biweekly' | 'week' | 'day'

type ArInvoice = {
  key: string
  customerId: string
  customer: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  dueDateSource: 'invoice_due_date' | 'term_days' | 'sales_term_name' | null
  originalAmount: number
  salesAmount: number
  applied: Array<{ date: string; amount: number }>
  allocationMethod: 'direct' | 'fifo'
  leftCensored: boolean
}

type ArUnappliedCredit = {
  key: string
  customerId: string
  customer: string
  transactionNumber: string
  transactionDate: string
  dueDate: string
  originalAmount: number
  applied: Array<{ date: string; amount: number }>
}

type ArSalesAdjustment = {
  date: string
  amount: number
  customerId: string
  customer: string
}

type ArPaymentApplication = {
  key: string
  date: string
  amount: number
  customerId: string
  customer: string
  transactionNumber: string
}

type ArLedgerEvent = {
  date: string
  amount: number
  customerId: string
  customer: string
  source: string
  sourceEntity: string
  sourceId: string
  transactionTypeId: string
  invoiceId: string
  sourceNum: string
  memo: string
}

type AuthoritativeEndingBalances = Record<string, number> | Map<string, number>
type PeriodSalesControls = Record<string, number> | Map<string, number>
type ArCustomerExclusion = {
  customerId?: string | null
  customer: string
  endingAr?: number | null
  invoiceCount?: number | null
}

type ArBucketAmounts = {
  currentAr: number
  under90Ar: number
  over90Ar: number
  over60Ar: number
  missingDueAr: number
  weightedAverageAge: number | null
  source: 'aging_snapshot' | 'invoice_rollforward' | 'unreconciled_aging_snapshot' | 'unreconciled_activity'
}

type ReconciledArBucketAmounts = ArBucketAmounts & {
  sourceBucketTotalAr: number
  agingBucketTotalAr: number
  unclassifiedAr: number
  sourceExcessAr: number
  bucketReconciliationAr: number
  knownReconcilingDifferenceAr: number
}

const cleanText = (value: any): string => String(value ?? '').trim()
const customerExclusionKey = (value: any): string => cleanText(value).toLowerCase()

const numberValue = (value: any): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const parsed = Number.parseFloat(cleanText(value).replace(/[,$]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const dateKey = (value: any): string => {
  const text = cleanText(value).slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : ''
}

const addDays = (date: string, days: number): string => {
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  parsed.setUTCDate(parsed.getUTCDate() + days)
  return parsed.toISOString().slice(0, 10)
}

const addMonths = (date: string, months: number): string => {
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  parsed.setUTCMonth(parsed.getUTCMonth() + months)
  return parsed.toISOString().slice(0, 10)
}

const monthStart = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1)).toISOString().slice(0, 10)
}

const formatUtcDateKey = (date: Date): string => (
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
)

const parseUtcDate = (value: any): Date | null => {
  const key = dateKey(value)
  if (!key) return null
  const parsed = new Date(`${key}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const startOfUtcWeek = (date: Date): Date => {
  const out = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const day = out.getUTCDay()
  const offset = day === 0 ? -6 : 1 - day
  out.setUTCDate(out.getUTCDate() + offset)
  return out
}

const normalizePeriodGrain = (raw: any): ArPeriodGrain => {
  const value = String(raw || '').trim().toLowerCase()
  if (value === 'quarter' || value === 'year' || value === 'biweekly' || value === 'week' || value === 'day') return value
  return 'month'
}

const daysBetween = (start: string, end: string): number => {
  const a = new Date(`${start}T00:00:00Z`).getTime()
  const b = new Date(`${end}T00:00:00Z`).getTime()
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  return Math.max(0, Math.round((b - a) / 86400000))
}

const amountForRow = (row: ArSourceRow): number => {
  const debit = numberValue(row.debit)
  const credit = numberValue(row.credit)
  return Math.abs(debit) > 0.000001 || Math.abs(credit) > 0.000001
    ? debit - credit
    : numberValue(row.amount)
}

const arSourceRowKey = (row: ArSourceRow): string => [
  dateKey(row.txn_date),
  cleanText(row.transaction_type),
  cleanText(row.transaction_type_id),
  cleanText(row.num),
  cleanText(row.account_id),
  cleanText(row.customer_id),
  cleanText(row.name),
  cleanText(row.invoice_id),
  cleanText(row.invoice_num),
  cleanText(row.debit),
  cleanText(row.credit),
  cleanText(row.amount),
  cleanText(row.open_balance),
  cleanText(row.memo),
  cleanText(row.source),
  cleanText(row.source_confidence),
  cleanText(row.source_entity),
  cleanText(row.source_id),
].join('|')

const uniqueArSourceRows = (rows: ArSourceRow[]): ArSourceRow[] => {
  const seen = new Set<string>()
  const unique: ArSourceRow[] = []
  for (const row of rows) {
    const key = arSourceRowKey(row)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(row)
  }
  return unique
}

const rowSource = (row: ArSourceRow): string => cleanText(row.source).toLowerCase()

const isStandaloneAgingSnapshotRow = (row: ArSourceRow): boolean => rowSource(row) === 'quickbooksagingsnapshot'

const isOpeningAgingDetailRow = (row: ArSourceRow): boolean => rowSource(row) === 'quickbooksagingdetail'

const isOpeningDifferenceRow = (row: ArSourceRow): boolean => rowSource(row) === 'quickbooksaropeningdifference'

const isAgingBucketSourceRow = (row: ArSourceRow): boolean => {
  const source = rowSource(row)
  return source === 'quickbooksagingsnapshot' || source === 'quickbooksagingdetail'
}

const isNonLedgerAgingBasisRow = (row: ArSourceRow): boolean => (
  isStandaloneAgingSnapshotRow(row)
  || rowSource(row) === 'quickbooksinvoiceapplication'
)

const ledgerEventsFromRows = (rows: ArSourceRow[]): ArLedgerEvent[] => {
  const fullHistoryInvoiceKeys = fullHistoryInvoiceKeysForRows(rows)
  return rows
  .filter((row) => !isNonLedgerAgingBasisRow(row))
  .filter((row) => !(isOpeningAgingDetailRow(row) && fullHistoryInvoiceKeys.has(invoiceKeyForRow(row))))
  .map((row) => {
    const date = dateKey(row.txn_date)
    return {
      date,
      amount: amountForRow(row),
      customerId: cleanText(row.customer_id) || cleanText(row.customer) || 'Unassigned customer',
	      customer: cleanText(row.customer) || 'Unassigned customer',
	      source: rowSource(row),
	      sourceEntity: cleanText(row.source_entity).toLowerCase(),
	      sourceId: cleanText(row.source_id),
	      transactionTypeId: cleanText(row.transaction_type_id),
	      invoiceId: cleanText(row.invoice_id),
	      sourceNum: cleanText(row.num),
	      memo: cleanText(row.memo),
	    }
	  })
  .filter((event) => event.date && Math.abs(event.amount) > 0.005)
  .sort((a, b) => a.date.localeCompare(b.date))
}

const parseTermDays = (row: ArSourceRow): { days: number | null; source: 'term_days' | 'sales_term_name' | null } => {
  const rawTermDays = cleanText(row.term_days)
  if (rawTermDays) {
    const days = Math.round(numberValue(rawTermDays))
    if (Number.isFinite(days) && days >= 0) return { days, source: 'term_days' }
  }
  const termText = [row.sales_term_name, row.terms_source]
    .map(cleanText)
    .find((value) => value)
    ?.toLowerCase()
    .replace(/\s+/g, ' ')
    || ''
  if (!termText) return { days: null, source: null }
  if (/due\s+on\s+receipt|upon\s+receipt|cod|cash\s+on\s+delivery/.test(termText)) {
    return { days: 0, source: 'sales_term_name' }
  }
  const netDays = termText.match(/\b(?:net|n)\s*-?\s*(\d{1,3})\b/)
  if (netDays) return { days: Number(netDays[1]), source: 'sales_term_name' }
  const discountWindow = termText.match(/\b\d+\s*\/\s*(\d{1,3})\b/)
  if (discountWindow) return { days: Number(discountWindow[1]), source: 'sales_term_name' }
  const dueDays = termText.match(/\bdue(?:\s+in)?\s+(\d{1,3})\s+days?\b/)
  if (dueDays) return { days: Number(dueDays[1]), source: 'sales_term_name' }
  const daysText = termText.match(/\b(\d{1,3})\s+days?\b/)
  if (daysText) return { days: Number(daysText[1]), source: 'sales_term_name' }
  return { days: null, source: null }
}

const openOn = (invoice: ArInvoice, date: string): number => (
  invoice.invoiceDate > date
    ? 0
    : Math.max(0, invoice.originalAmount - invoice.applied
      .filter((event) => event.date <= date)
      .reduce((sum, event) => sum + event.amount, 0))
)

const creditOpenOn = (credit: ArUnappliedCredit, date: string): number => (
  credit.transactionDate > date
    ? 0
    : Math.max(0, credit.originalAmount - credit.applied
      .filter((event) => event.date <= date)
      .reduce((sum, event) => sum + event.amount, 0))
)

const periodKeyForDate = (date: Date, grain: ArPeriodGrain): string => {
  if (grain === 'year') return String(date.getUTCFullYear())
  if (grain === 'quarter') return `${date.getUTCFullYear()}-Q${Math.floor(date.getUTCMonth() / 3) + 1}`
  if (grain === 'week' || grain === 'biweekly') {
    const weekStart = startOfUtcWeek(date)
    if (grain === 'biweekly') {
      const epoch = Date.UTC(1970, 0, 5)
      const weeks = Math.floor((weekStart.getTime() - epoch) / (7 * 86400000))
      if (weeks % 2) weekStart.setUTCDate(weekStart.getUTCDate() - 7)
    }
    return formatUtcDateKey(weekStart)
  }
  if (grain === 'day') return formatUtcDateKey(date)
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const periodRangeForKey = (period: string, grain: ArPeriodGrain, asOfDate: string): { start: string; end: string } | null => {
  const asOf = parseUtcDate(asOfDate) || new Date()
  if (grain === 'year') {
    const year = Math.max(1900, Math.floor(Number(period) || asOf.getUTCFullYear()))
    const start = new Date(Date.UTC(year, 0, 1))
    const end = new Date(Date.UTC(year, 11, 31))
    if (end > asOf) end.setTime(asOf.getTime())
    return { start: formatUtcDateKey(start), end: formatUtcDateKey(end) }
  }
  if (grain === 'quarter') {
    const match = /^(\d{4})-Q([1-4])$/.exec(period)
    const year = match ? Number(match[1]) : asOf.getUTCFullYear()
    const quarter = match ? Number(match[2]) : Math.floor(asOf.getUTCMonth() / 3) + 1
    const start = new Date(Date.UTC(year, (quarter - 1) * 3, 1))
    const end = new Date(start)
    end.setUTCMonth(end.getUTCMonth() + 3)
    end.setUTCDate(end.getUTCDate() - 1)
    if (end > asOf) end.setTime(asOf.getTime())
    return { start: formatUtcDateKey(start), end: formatUtcDateKey(end) }
  }
  const start = parseUtcDate(period)
  if (!start) return null
  const end = new Date(start)
  if (grain === 'day') {
    // Same-day inclusive period.
  } else if (grain === 'week') {
    end.setUTCDate(end.getUTCDate() + 6)
  } else if (grain === 'biweekly') {
    end.setUTCDate(end.getUTCDate() + 13)
  } else {
    end.setUTCMonth(end.getUTCMonth() + 1)
    end.setUTCDate(end.getUTCDate() - 1)
  }
  if (end > asOf) end.setTime(asOf.getTime())
  return { start: formatUtcDateKey(start), end: formatUtcDateKey(end) }
}

const periodsForGrain = (asOfDate: string, periodCount: number, rawGrain: any = 'month'): Array<{ start: string; end: string; key: string }> => {
  const grain = normalizePeriodGrain(rawGrain)
  const asOf = new Date(`${dateKey(asOfDate) || new Date().toISOString().slice(0, 10)}T00:00:00Z`)
  const count = Math.max(1, Math.min(24, Math.floor(Number(periodCount) || 13)))
  const periods: Array<{ start: string; end: string; key: string }> = []
  if (grain === 'year') {
    const endYear = asOf.getUTCFullYear()
    for (let index = count - 1; index >= 0; index -= 1) {
      const key = String(endYear - index)
      const range = periodRangeForKey(key, grain, formatUtcDateKey(asOf))
      if (range) periods.push({ ...range, key })
    }
    return periods
  }
  if (grain === 'quarter') {
    const end = new Date(Date.UTC(asOf.getUTCFullYear(), Math.floor(asOf.getUTCMonth() / 3) * 3, 1))
    for (let index = count - 1; index >= 0; index -= 1) {
      const date = new Date(end)
      date.setUTCMonth(end.getUTCMonth() - index * 3)
      const key = periodKeyForDate(date, grain)
      const range = periodRangeForKey(key, grain, formatUtcDateKey(asOf))
      if (range) periods.push({ ...range, key })
    }
    return periods
  }
  if (grain === 'day') {
    const end = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate()))
    for (let index = count - 1; index >= 0; index -= 1) {
      const date = new Date(end)
      date.setUTCDate(end.getUTCDate() - index)
      const key = periodKeyForDate(date, grain)
      const range = periodRangeForKey(key, grain, formatUtcDateKey(asOf))
      if (range) periods.push({ ...range, key })
    }
    return periods
  }
  if (grain === 'week' || grain === 'biweekly') {
    const endKey = periodKeyForDate(asOf, grain)
    const end = parseUtcDate(endKey) || startOfUtcWeek(asOf)
    const stepDays = grain === 'biweekly' ? 14 : 7
    for (let index = count - 1; index >= 0; index -= 1) {
      const date = new Date(end)
      date.setUTCDate(end.getUTCDate() - index * stepDays)
      const key = periodKeyForDate(date, grain)
      const range = periodRangeForKey(key, grain, formatUtcDateKey(asOf))
      if (range) periods.push({ ...range, key })
    }
    return periods
  }
  const endMonth = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), 1))
  for (let index = count - 1; index >= 0; index -= 1) {
    const date = new Date(endMonth)
    date.setUTCMonth(endMonth.getUTCMonth() - index)
    const key = periodKeyForDate(date, grain)
    const range = periodRangeForKey(key, grain, formatUtcDateKey(asOf))
    if (range) periods.push({ ...range, key })
  }
  return periods
}

const invoiceKeyForRow = (row: ArSourceRow, fallbackIndex: number | null = null): string => {
  const txnDate = dateKey(row.txn_date)
  const customer = cleanText(row.customer) || 'Unassigned customer'
  const customerId = cleanText(row.customer_id) || customer
  const invoiceDate = dateKey(row.invoice_date) || txnDate
  const invoiceNumber = cleanText(row.invoice_num) || cleanText(row.num)
  const invoiceId = cleanText(row.invoice_id)
  if (invoiceId) return `invoice:${invoiceId}`
  if (invoiceNumber) return `invoice-num:${customerId}:${invoiceNumber}:${invoiceDate}`
  return fallbackIndex == null ? '' : `synthetic:${customerId}:${txnDate}:${fallbackIndex}`
}

const rowIsFullHistoryInvoiceOrigin = (row: ArSourceRow): boolean => {
  if (isOpeningAgingDetailRow(row) || isStandaloneAgingSnapshotRow(row) || isOpeningDifferenceRow(row)) return false
  const amount = amountForRow(row)
  if (amount <= 0.005) return false
  const txnType = cleanText(row.transaction_type).toLowerCase()
  const sourceEntity = cleanText(row.source_entity).toLowerCase()
  return txnType.includes('invoice') || sourceEntity === 'invoice'
}

const fullHistoryInvoiceKeysForRows = (rows: ArSourceRow[]): Set<string> => new Set(
  rows
    .filter(rowIsFullHistoryInvoiceOrigin)
    .map((row) => invoiceKeyForRow(row))
    .filter(Boolean),
)

const arRollforwardRowSortRank = (row: ArSourceRow): number => {
  if (isStandaloneAgingSnapshotRow(row) || isOpeningDifferenceRow(row)) return 9
  const amount = amountForRow(row)
  const txnType = cleanText(row.transaction_type).toLowerCase()
  const sourceEntity = cleanText(row.source_entity).toLowerCase()
  if (amount > 0.005 && (
    isOpeningAgingDetailRow(row)
    || txnType.includes('invoice')
    || sourceEntity === 'invoice'
  )) return 0
  if (amount < -0.005) return 1
  return 5
}

const applicationAmountKey = (amount: number): string => Math.round(Math.abs(amount) * 100).toString()

const creditMemoOffsetKey = (customerId: string, customer: string, invoiceNumber: string, amount: number): string => [
  customerExclusionKey(customerId || customer),
  cleanText(invoiceNumber).toLowerCase(),
  applicationAmountKey(amount),
].join('|')

const creditMemoOffsetDatesByApplicationKey = (rows: ArSourceRow[]): Map<string, string[]> => {
  const offsets = new Map<string, string[]>()
  for (const row of rows) {
    const txnDate = dateKey(row.txn_date)
    const amount = amountForRow(row)
    const sourceEntity = cleanText(row.source_entity).toLowerCase()
    const txnType = cleanText(row.transaction_type).toLowerCase()
    if (!txnDate || amount >= -0.005 || (sourceEntity !== 'creditmemo' && !txnType.includes('credit'))) continue
    const creditNumber = cleanText(row.num) || cleanText(row.invoice_num)
    const invoiceNumber = creditNumber.replace(/^cm\s*/i, '')
    if (!invoiceNumber || invoiceNumber === creditNumber) continue
    const customer = cleanText(row.customer) || 'Unassigned customer'
    const customerId = cleanText(row.customer_id) || customer
    const key = creditMemoOffsetKey(customerId, customer, invoiceNumber, amount)
    offsets.set(key, [...(offsets.get(key) || []), txnDate].sort())
  }
  return offsets
}

const reconstructInvoices = (rows: ArSourceRow[]): { invoices: ArInvoice[]; credits: ArUnappliedCredit[]; warnings: string[] } => {
  const invoices: ArInvoice[] = []
  const credits: ArUnappliedCredit[] = []
  const byKey = new Map<string, ArInvoice>()
  const pendingApplications = new Map<string, ArPaymentApplication[]>()
  const fullHistoryInvoiceKeys = fullHistoryInvoiceKeysForRows(rows)
  const creditMemoOffsetDates = creditMemoOffsetDatesByApplicationKey(rows)
  let skippedOpeningSeeds = 0
  let skippedUnlinkedApplications = 0
  const addCredit = (
    application: ArPaymentApplication,
    appliedDate: string | null = null,
    appliedAmount = 0,
  ): void => {
    credits.push({
      key: `credit:${application.key}:${credits.length}`,
      customerId: application.customerId,
      customer: application.customer,
      transactionNumber: application.transactionNumber,
      transactionDate: application.date,
      dueDate: application.date,
      originalAmount: Math.max(0, application.amount),
      applied: appliedDate && appliedAmount > 0.005 ? [{ date: appliedDate, amount: appliedAmount }] : [],
    })
  }
  const applyToInvoice = (invoice: ArInvoice, application: ArPaymentApplication): number => {
    let remaining = Math.max(0, application.amount)
    const applicationDate = application.date < invoice.invoiceDate ? invoice.invoiceDate : application.date
    const applied = Math.min(remaining, openOn(invoice, applicationDate))
    if (application.date < applicationDate) {
      addCredit(application, applicationDate, applied)
      if (applied > 0) invoice.applied.push({ date: applicationDate, amount: applied })
      return 0
    }
    if (applied > 0) {
      invoice.applied.push({ date: applicationDate, amount: applied })
      remaining -= applied
    }
    return remaining
  }
  const drainPendingApplications = (key: string, invoice: ArInvoice): void => {
    const pending = pendingApplications.get(key)
    if (!pending?.length) return
    const stillPending: ArPaymentApplication[] = []
    for (const application of pending.sort((a, b) => a.date.localeCompare(b.date))) {
      const remaining = applyToInvoice(invoice, application)
      if (remaining > 0.005) stillPending.push({ ...application, amount: remaining })
    }
    if (stillPending.length) pendingApplications.set(key, stillPending)
    else pendingApplications.delete(key)
  }
  for (const row of [...rows].sort((a, b) => (
    dateKey(a.txn_date).localeCompare(dateKey(b.txn_date))
    || arRollforwardRowSortRank(a) - arRollforwardRowSortRank(b)
    || cleanText(a.source_entity).localeCompare(cleanText(b.source_entity))
    || cleanText(a.source_id).localeCompare(cleanText(b.source_id), undefined, { numeric: true, sensitivity: 'base' })
    || cleanText(a.transaction_type_id).localeCompare(cleanText(b.transaction_type_id), undefined, { numeric: true, sensitivity: 'base' })
  ))) {
    if (isStandaloneAgingSnapshotRow(row) || isOpeningDifferenceRow(row)) continue
    const txnDate = dateKey(row.txn_date)
    if (!txnDate) continue
    const amount = amountForRow(row)
    if (Math.abs(amount) < 0.005) continue
    const customer = cleanText(row.customer) || 'Unassigned customer'
    const customerId = cleanText(row.customer_id) || customer
    const invoiceDate = dateKey(row.invoice_date) || txnDate
    const explicitDueDate = dateKey(row.invoice_due_date)
    const termDays = parseTermDays(row)
    const dueDate = explicitDueDate || (termDays.days != null ? addDays(invoiceDate, termDays.days) : '')
    const dueDateSource = explicitDueDate ? 'invoice_due_date' : termDays.source
    const invoiceNumber = cleanText(row.invoice_num) || cleanText(row.num)
    const key = invoiceKeyForRow(row, invoices.length)
    const txnType = cleanText(row.transaction_type).toLowerCase()
    const sourceEntity = cleanText(row.source_entity).toLowerCase()
    const isInvoiceOrigin = amount > 0 && (
      isOpeningAgingDetailRow(row)
      || txnType.includes('invoice')
      || sourceEntity === 'invoice'
    )
    if (isInvoiceOrigin) {
      if (isOpeningAgingDetailRow(row) && fullHistoryInvoiceKeys.has(invoiceKeyForRow(row))) {
        const existing = byKey.get(key)
        if (existing) {
          if (!existing.dueDate) existing.dueDate = dueDate
          if (!existing.dueDateSource) existing.dueDateSource = dueDateSource
          if (!existing.invoiceNumber) existing.invoiceNumber = invoiceNumber
        }
        skippedOpeningSeeds += 1
        continue
      }
      const existing = byKey.get(key)
      if (existing) {
        existing.originalAmount += amount
        if (!isOpeningAgingDetailRow(row)) existing.salesAmount += amount
        if (!existing.dueDate) existing.dueDate = dueDate
        if (!existing.dueDateSource) existing.dueDateSource = dueDateSource
        if (!isOpeningAgingDetailRow(row)) existing.leftCensored = false
        drainPendingApplications(key, existing)
      } else {
        const leftCensored = isOpeningAgingDetailRow(row)
        const invoice: ArInvoice = {
          key,
          customerId,
          customer,
          invoiceNumber,
          invoiceDate,
          dueDate,
          dueDateSource,
          originalAmount: amount,
          salesAmount: leftCensored ? 0 : amount,
          applied: [],
          allocationMethod: 'direct',
          leftCensored,
        }
        byKey.set(key, invoice)
        invoices.push(invoice)
        drainPendingApplications(key, invoice)
      }
      continue
    }
    if (amount >= 0) continue
    const transactionNumber = cleanText(row.num) || cleanText(row.invoice_num) || cleanText(row.transaction_type_id) || cleanText(row.source_id)
    const offsetKey = creditMemoOffsetKey(customerId, customer, invoiceNumber, amount)
    const creditMemoOffsetDate = (creditMemoOffsetDates.get(offsetKey) || []).find((date) => date >= txnDate)
    const application: ArPaymentApplication = {
      key,
      date: creditMemoOffsetDate || txnDate,
      amount: Math.abs(amount),
      customerId,
      customer,
      transactionNumber,
    }
    let remaining = application.amount
    const direct = byKey.get(key)
    if (direct) {
      remaining = applyToInvoice(direct, application)
    }
    if (remaining > 0.005) {
      const pending = pendingApplications.get(key) || []
      pending.push({ ...application, amount: remaining })
      pendingApplications.set(key, pending)
    }
  }
  skippedUnlinkedApplications = Array.from(pendingApplications.values()).reduce((sum, entries) => sum + entries.length, 0)
  const missingDue = invoices.filter((invoice) => !invoice.dueDate).length
  const warnings: string[] = []
  if (missingDue) warnings.push(`${missingDue} receivable invoice(s) are missing due dates or payment terms; they are included in DSO but excluded from overdue scoring.`)
  if (skippedOpeningSeeds) warnings.push(`${skippedOpeningSeeds} opening A/R aging seed row(s) matched invoices already reconstructed from full invoice-origin history, so they were skipped to avoid double-counting.`)
  if (credits.length) warnings.push(`${credits.length} pre-invoice payment or credit row(s) are represented as temporary negative A/R balances until the linked invoice exists, matching QuickBooks aging report treatment.`)
  if (skippedUnlinkedApplications) warnings.push(`${skippedUnlinkedApplications} payment or credit application row(s) did not have a resolvable invoice link, so they were left unallocated in diagnostics instead of being guessed into aging buckets.`)
  return {
    invoices,
    credits,
    warnings,
  }
}

const addAmountToDueDateBucket = (
  amount: number,
  dueDate: string,
  end: string,
  buckets: { currentAr: number; under90Ar: number; over90Ar: number; over60Ar: number },
): void => {
  const pastDueDays = daysBetween(dueDate, end)
  if (dueDate >= end) {
    buckets.currentAr += amount
  } else if (pastDueDays <= 90) {
    buckets.under90Ar += amount
    if (pastDueDays > 60) buckets.over60Ar += amount
  } else {
    buckets.over90Ar += amount
    buckets.over60Ar += amount
  }
}

const arBucketAmounts = (invoices: ArInvoice[], credits: ArUnappliedCredit[], end: string): ArBucketAmounts => {
  let currentAr = 0
  let under90Ar = 0
  let over90Ar = 0
  let over60Ar = 0
  let missingDueAr = 0
  let ageWeightedOpen = 0
  for (const invoice of invoices) {
    const open = openOn(invoice, end)
    if (open <= 0.005) continue
    const daysOpen = Math.max(0, daysBetween(invoice.invoiceDate, end))
    ageWeightedOpen += open * daysOpen
    if (invoice.dueDate) {
      const buckets = { currentAr, under90Ar, over90Ar, over60Ar }
      addAmountToDueDateBucket(open, invoice.dueDate, end, buckets)
      currentAr = buckets.currentAr
      under90Ar = buckets.under90Ar
      over90Ar = buckets.over90Ar
      over60Ar = buckets.over60Ar
      continue
    }
    missingDueAr += open
    if (daysOpen <= 90) {
      under90Ar += open
      if (daysOpen > 60) over60Ar += open
    } else {
      over90Ar += open
      over60Ar += open
    }
  }
  for (const credit of credits) {
    const open = creditOpenOn(credit, end)
    if (open <= 0.005) continue
    const amount = -open
    const daysOpen = Math.max(0, daysBetween(credit.transactionDate, end))
    ageWeightedOpen += amount * daysOpen
    const buckets = { currentAr, under90Ar, over90Ar, over60Ar }
    addAmountToDueDateBucket(amount, credit.dueDate || credit.transactionDate, end, buckets)
    currentAr = buckets.currentAr
    under90Ar = buckets.under90Ar
    over90Ar = buckets.over90Ar
    over60Ar = buckets.over60Ar
  }
  const totalAr = currentAr + under90Ar + over90Ar
  return {
    currentAr,
    under90Ar,
    over90Ar,
    over60Ar,
    missingDueAr,
    weightedAverageAge: totalAr > 0.005 ? ageWeightedOpen / totalAr : null,
    source: 'invoice_rollforward' as const,
  }
}

const agingSnapshotBucketAmounts = (rows: ArSourceRow[], end: string): ArBucketAmounts => {
  let currentAr = 0
  let under90Ar = 0
  let over90Ar = 0
  let over60Ar = 0
  let missingDueAr = 0
  let ageWeightedOpen = 0
  for (const row of rows) {
    if (!isAgingBucketSourceRow(row) || dateKey(row.txn_date) !== end) continue
    const open = amountForRow(row)
    if (Math.abs(open) <= 0.005) continue
    const invoiceDate = dateKey(row.invoice_date) || dateKey(row.txn_date) || end
    const dueDate = dateKey(row.invoice_due_date)
    ageWeightedOpen += open * daysBetween(invoiceDate, end)
    if (dueDate) {
      const pastDueDays = daysBetween(dueDate, end)
      if (dueDate >= end) currentAr += open
      else if (pastDueDays <= 90) {
        under90Ar += open
        if (pastDueDays > 60) over60Ar += open
      } else {
        over90Ar += open
        over60Ar += open
      }
    } else {
      missingDueAr += open
      if (daysBetween(invoiceDate, end) <= 90) under90Ar += open
      else {
        over90Ar += open
        over60Ar += open
      }
    }
  }
  const totalAr = currentAr + under90Ar + over90Ar
  return {
    currentAr,
    under90Ar,
    over90Ar,
    over60Ar,
    missingDueAr,
    weightedAverageAge: totalAr > 0.005 ? ageWeightedOpen / totalAr : null,
    source: 'aging_snapshot' as const,
  }
}

const bucketTotalAr = (buckets: Pick<ArBucketAmounts, 'currentAr' | 'under90Ar' | 'over90Ar'>): number => (
  buckets.currentAr + buckets.under90Ar + buckets.over90Ar
)

const ledgerBalanceOn = (events: ArLedgerEvent[], date: string): number => (
  events.reduce((sum, event) => event.date <= date ? sum + event.amount : sum, 0)
)

const ledgerOpeningDifferenceOn = (events: ArLedgerEvent[], date: string): number => (
  events.reduce((sum, event) => (
    event.source === 'quickbooksaropeningdifference' && event.date <= date
      ? sum + event.amount
      : sum
  ), 0)
)

const nonInvoiceArBalanceOn = (events: ArLedgerEvent[], date: string): number => (
  events.reduce((sum, event) => {
    if (event.date > date) return sum
    if (event.source !== 'quickbooksentityhistory') return sum
    if (event.sourceEntity === 'journalentry') return sum + event.amount
    return sum
  }, 0)
)

const invoiceBalanceOn = (invoices: ArInvoice[], date: string): number => (
  invoices.reduce((sum, invoice) => sum + openOn(invoice, date), 0)
)

const creditBalanceOn = (credits: ArUnappliedCredit[], date: string): number => (
  credits.reduce((sum, credit) => sum + creditOpenOn(credit, date), 0)
)

const shouldExcludeCustomer = (
  customerId: any,
  customer: any,
  exclusions: Set<string>,
): boolean => {
  if (!exclusions.size) return false
  return exclusions.has(customerExclusionKey(customerId)) || exclusions.has(customerExclusionKey(customer))
}

const ledgerEntityKeysForRow = (row: ArSourceRow): string[] => [
  cleanText(row.source_id),
  cleanText(row.transaction_type_id),
  cleanText(row.invoice_id),
  cleanText(row.num),
  cleanText(row.invoice_num),
].filter(Boolean)

const ledgerEntityKeysForEvent = (event: ArLedgerEvent): string[] => {
  const keys = [
    event.sourceId,
    event.transactionTypeId,
    event.invoiceId,
    event.sourceNum,
  ].filter(Boolean)
  if (event.sourceEntity === 'journalentry' && event.sourceNum.endsWith('R') && event.sourceNum.length > 1) {
    keys.push(event.sourceNum.slice(0, -1))
  }
  const reverseMatch = event.memo.match(/reverse\s+of\s+je\s+([^\s-]+)/i)
  if (reverseMatch?.[1]) keys.push(reverseMatch[1].trim())
  return keys
}

const ledgerEntityCustomers = (
  rows: ArSourceRow[],
  exclusions: Set<string> = new Set(),
): Map<string, { customerId: string; customer: string }> => {
  const byKey = new Map<string, { customerId: string; customer: string }>()
  for (const row of rows) {
    if (exclusions.size && !shouldExcludeCustomer(row.customer_id, row.customer, exclusions)) continue
    const customerId = cleanText(row.customer_id) || cleanText(row.customer) || 'Unassigned customer'
    const customer = cleanText(row.customer) || customerId
    if (!customer || customer === 'Unassigned customer') continue
    for (const key of ledgerEntityKeysForRow(row)) byKey.set(key, { customerId, customer })
  }
  return byKey
}

const excludedLedgerEntityCustomers = (
  rows: ArSourceRow[],
  exclusions: Set<string>,
): Map<string, { customerId: string; customer: string }> => (
  exclusions.size ? ledgerEntityCustomers(rows, exclusions) : new Map()
)

const excludedCustomerForLedgerEvent = (
  event: ArLedgerEvent,
  excludedEntityCustomers: Map<string, { customerId: string; customer: string }>,
): { customerId: string; customer: string } | null => {
  for (const key of ledgerEntityKeysForEvent(event)) {
    const customer = excludedEntityCustomers.get(key)
    if (customer) return customer
  }
  return null
}

const shouldExcludeLedgerEvent = (
  event: ArLedgerEvent,
  exclusions: Set<string>,
  excludedEntityCustomers: Map<string, { customerId: string; customer: string }>,
): boolean => {
  if (!exclusions.size) return false
  return shouldExcludeCustomer(event.customerId, event.customer, exclusions)
    || Boolean(excludedCustomerForLedgerEvent(event, excludedEntityCustomers))
}

const excludedCustomerSummaries = (
  invoices: ArInvoice[],
  ledgerEvents: ArLedgerEvent[],
  asOfDate: string,
  exclusions: Set<string>,
  excludedEntityCustomers: Map<string, { customerId: string; customer: string }> = new Map(),
): ArCustomerExclusion[] => {
  if (!exclusions.size) return []
  const byKey = new Map<string, ArCustomerExclusion>()
  for (const invoice of invoices) {
    if (!shouldExcludeCustomer(invoice.customerId, invoice.customer, exclusions)) continue
    const key = customerExclusionKey(invoice.customerId) || customerExclusionKey(invoice.customer)
    const current = byKey.get(key) || {
      customerId: invoice.customerId,
      customer: invoice.customer || 'Unassigned customer',
      endingAr: 0,
      invoiceCount: 0,
    }
    current.endingAr = Number(current.endingAr || 0) + openOn(invoice, asOfDate)
    current.invoiceCount = Number(current.invoiceCount || 0) + 1
    byKey.set(key, current)
  }
  for (const event of ledgerEvents) {
    const eventCustomer = shouldExcludeCustomer(event.customerId, event.customer, exclusions)
      ? { customerId: event.customerId, customer: event.customer }
      : excludedCustomerForLedgerEvent(event, excludedEntityCustomers)
    if (!eventCustomer) continue
    const key = customerExclusionKey(eventCustomer.customerId) || customerExclusionKey(eventCustomer.customer)
    const current = byKey.get(key) || {
      customerId: eventCustomer.customerId,
      customer: eventCustomer.customer || 'Unassigned customer',
      endingAr: 0,
      invoiceCount: 0,
    }
    current.endingAr = Number(current.endingAr || 0) + nonInvoiceArBalanceOn([event], asOfDate)
    byKey.set(key, current)
  }
  return [...byKey.values()].sort((a, b) => Math.abs(Number(b.endingAr || 0)) - Math.abs(Number(a.endingAr || 0)))
}

const paidDateForInvoice = (invoice: ArInvoice): string | null => {
  let paid = 0
  for (const event of [...invoice.applied].sort((a, b) => a.date.localeCompare(b.date))) {
    paid += Math.max(0, event.amount)
    if (paid >= invoice.originalAmount - 0.005) return event.date
  }
  return null
}

const paidAfterDate = (invoice: ArInvoice, date: string): number => (
  invoice.applied
    .filter((event) => event.date > date)
    .reduce((sum, event) => sum + Math.max(0, event.amount), 0)
)

const invoiceSalesBetween = (invoices: ArInvoice[], start: string, end: string): number => (
  invoices
    .filter((invoice) => invoice.invoiceDate >= start && invoice.invoiceDate <= end)
    .reduce((sum, invoice) => sum + Math.max(0, Number(invoice.salesAmount || 0)), 0)
)

const salesAdjustmentRows = (rows: ArSourceRow[]): ArSalesAdjustment[] => rows.flatMap((row) => {
  const date = dateKey(row.txn_date)
  const amount = amountForRow(row)
  const txnType = cleanText(row.transaction_type).toLowerCase()
  const sourceEntity = cleanText(row.source_entity).toLowerCase()
  const isRevenueCredit = amount < -0.005 && (
    sourceEntity === 'creditmemo'
    || txnType.includes('credit memo')
    || txnType.includes('creditmemo')
    || txnType.includes('refund')
  )
  if (!date || !isRevenueCredit) return []
  const customer = cleanText(row.customer) || 'Unassigned customer'
  const customerId = cleanText(row.customer_id) || customer
  return [{
    date,
    amount,
    customerId,
    customer,
  }]
})

const netSalesBetween = (
  invoices: ArInvoice[],
  salesAdjustments: ArSalesAdjustment[],
  start: string,
  end: string,
): number => (
  invoiceSalesBetween(invoices, start, end)
  + salesAdjustments
    .filter((event) => event.date >= start && event.date <= end)
    .reduce((sum, event) => sum + Number(event.amount || 0), 0)
)

const percentileNumber = (values: number[], percentile: number): number | null => {
  const xs = values.filter(Number.isFinite).sort((a, b) => a - b)
  if (!xs.length) return null
  const index = Math.min(xs.length - 1, Math.max(0, Math.ceil((percentile / 100) * xs.length) - 1))
  return xs[index]
}

const collectabilityThresholds = [90, 180, 270, 360]

const thresholdForPastDueDays = (daysPastDue: number | null | undefined): number | null => {
  const days = Number(daysPastDue)
  if (!Number.isFinite(days)) return null
  let selected: number | null = null
  for (const threshold of collectabilityThresholds) {
    if (days >= threshold) selected = threshold
  }
  return selected
}

const collectabilityConfidence = (invoiceCount: number, amount: number): 'high' | 'medium' | 'low' => {
  if (invoiceCount >= 30 && amount >= 100000) return 'high'
  if (invoiceCount >= 10 && amount >= 25000) return 'medium'
  return 'low'
}

const buildCollectabilityAnalysis = (invoices: ArInvoice[], asOfDate: string) => {
  const warnings: string[] = []
  const benchmarkInvoices = invoices.filter((invoice) => !invoice.leftCensored && invoice.dueDate && invoice.originalAmount > 0.005)
  const skippedLeftCensored = invoices.filter((invoice) => invoice.leftCensored && invoice.dueDate).length
  const skippedMissingDue = invoices.filter((invoice) => !invoice.dueDate).length
  if (skippedLeftCensored) {
    warnings.push(`${skippedLeftCensored} opening A/R invoice(s) are included in current exposure but excluded from historical recovery benchmarks because pre-opening payment history is incomplete.`)
  }
  if (skippedMissingDue) {
    warnings.push(`${skippedMissingDue} invoice(s) are missing due dates or usable terms and are excluded from threshold cohorts.`)
  }
  const thresholds = collectabilityThresholds.map((thresholdDays) => {
    let cohortInvoiceCount = 0
    let fullyPaidInvoiceCount = 0
    let anyRecoveryInvoiceCount = 0
    let cohortOriginalAmount = 0
    let openAtThresholdAmount = 0
    let recoveredAfterThresholdAmount = 0
    let remainingAsOfAmount = 0
    const daysToFullPaymentAfterThreshold: number[] = []
    for (const invoice of benchmarkInvoices) {
      const thresholdDate = addDays(invoice.dueDate, thresholdDays)
      if (thresholdDate > asOfDate) continue
      const openAtThreshold = openOn(invoice, thresholdDate)
      if (openAtThreshold < 1) continue
      const paidAfter = Math.min(openAtThreshold, paidAfterDate(invoice, thresholdDate))
      const remainingAsOf = Math.max(0, openOn(invoice, asOfDate))
      const fullyPaidDate = paidDateForInvoice(invoice)
      const fullyPaidAfterThreshold = Boolean(fullyPaidDate && fullyPaidDate > thresholdDate && remainingAsOf <= 0.005)
      cohortInvoiceCount += 1
      cohortOriginalAmount += invoice.originalAmount
      openAtThresholdAmount += openAtThreshold
      recoveredAfterThresholdAmount += paidAfter
      remainingAsOfAmount += Math.max(0, openAtThreshold - paidAfter)
      if (paidAfter >= 1) anyRecoveryInvoiceCount += 1
      if (fullyPaidAfterThreshold) {
        fullyPaidInvoiceCount += 1
        daysToFullPaymentAfterThreshold.push(daysBetween(thresholdDate, fullyPaidDate!))
      }
    }
    const amountRecoveryRate = openAtThresholdAmount > 0.005 ? recoveredAfterThresholdAmount / openAtThresholdAmount : null
    const fullPaymentRate = cohortInvoiceCount ? fullyPaidInvoiceCount / cohortInvoiceCount : null
    const anyRecoveryRate = cohortInvoiceCount ? anyRecoveryInvoiceCount / cohortInvoiceCount : null
    return {
      thresholdDays,
      cohortInvoiceCount,
      fullyPaidInvoiceCount,
      anyRecoveryInvoiceCount,
      cohortOriginalAmount,
      openAtThresholdAmount,
      recoveredAfterThresholdAmount,
      remainingAsOfAmount,
      amountRecoveryRate,
      fullPaymentRate,
      anyRecoveryRate,
      medianDaysToFullPaymentAfterThreshold: percentileNumber(daysToFullPaymentAfterThreshold, 50),
      p75DaysToFullPaymentAfterThreshold: percentileNumber(daysToFullPaymentAfterThreshold, 75),
      confidence: collectabilityConfidence(cohortInvoiceCount, openAtThresholdAmount),
    }
  })
  const thresholdByDays = new Map(thresholds.map((entry) => [entry.thresholdDays, entry]))
  const exposureInvoicesForDate = (date: string) => invoices
    .flatMap((invoice) => {
      const openAmount = openOn(invoice, date)
      const daysPastDue = invoice.dueDate ? daysBetween(invoice.dueDate, date) : null
      const thresholdDays = thresholdForPastDueDays(daysPastDue)
      if (openAmount < 1 || thresholdDays == null) return []
      const benchmark = thresholdByDays.get(thresholdDays)
      const benchmarkRecoveryRate = benchmark?.amountRecoveryRate ?? null
      const expectedCollectibleAmount = benchmarkRecoveryRate == null ? null : openAmount * benchmarkRecoveryRate
      const expectedWriteOffAmount = expectedCollectibleAmount == null ? null : Math.max(0, openAmount - expectedCollectibleAmount)
      return [{
        invoiceKey: invoice.key,
        customerId: invoice.customerId,
        customer: invoice.customer,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate || null,
        openAmount,
        daysPastDue,
        thresholdDays,
        benchmarkRecoveryRate,
        expectedCollectibleAmount,
        expectedWriteOffAmount,
        confidence: benchmark?.confidence || 'low',
        leftCensored: invoice.leftCensored,
      }]
    })
    .sort((a: any, b: any) => Number(b.expectedWriteOffAmount ?? b.openAmount ?? 0) - Number(a.expectedWriteOffAmount ?? a.openAmount ?? 0))
  const currentInvoices = exposureInvoicesForDate(asOfDate)
  const currentAtRiskOpen = currentInvoices.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.openAmount || 0)), 0)
  const currentExpectedCollectible = currentInvoices.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.expectedCollectibleAmount || 0)), 0)
  const currentExpectedWriteOff = currentInvoices.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.expectedWriteOffAmount || 0)), 0)
  const asOfMonthStart = monthStart(asOfDate)
  const trailingSalesStart = monthStart(addMonths(asOfDate, -11))
  const currentMonthSales = invoiceSalesBetween(invoices, asOfMonthStart, asOfDate)
  const trailingTwelveMonthSales = invoiceSalesBetween(invoices, trailingSalesStart, asOfDate)
  const benchmarkedThresholds = thresholds.filter((threshold) => Number.isFinite(Number(threshold.amountRecoveryRate)) && Number(threshold.openAtThresholdAmount || 0) > 0.005)
  const cohortOpenAtThreshold = benchmarkedThresholds.reduce((sum, threshold) => sum + Math.max(0, Number(threshold.openAtThresholdAmount || 0)), 0)
  const cohortExpectedLoss = benchmarkedThresholds.reduce((sum, threshold) => {
    const recovery = Number(threshold.amountRecoveryRate || 0)
    return sum + Math.max(0, Number(threshold.openAtThresholdAmount || 0) * (1 - Math.max(0, Math.min(1, recovery))))
  }, 0)
  const cohortLossRate = cohortOpenAtThreshold > 0.005 ? cohortExpectedLoss / cohortOpenAtThreshold : null
  const ninetyThreshold = thresholdByDays.get(90)
  const simpleNinetyLossRate = ninetyThreshold?.amountRecoveryRate == null ? null : Math.max(0, 1 - Math.max(0, Math.min(1, Number(ninetyThreshold.amountRecoveryRate))))
  const currentReservePercentOfSales = trailingTwelveMonthSales > 0.005 ? currentExpectedWriteOff / trailingTwelveMonthSales : null
  const cohortReserveAmount = cohortLossRate == null ? null : currentAtRiskOpen * cohortLossRate
  const cohortPercentOfSales = trailingTwelveMonthSales > 0.005 && cohortReserveAmount != null ? cohortReserveAmount / trailingTwelveMonthSales : null
  const simpleNinetyReserveAmount = simpleNinetyLossRate == null ? null : currentAtRiskOpen * simpleNinetyLossRate
  const simpleNinetyPercentOfSales = trailingTwelveMonthSales > 0.005 && simpleNinetyReserveAmount != null ? simpleNinetyReserveAmount / trailingTwelveMonthSales : null
  const reserveTrend = periodsForGrain(asOfDate, 13, 'month').map((period) => {
    const exposure = exposureInvoicesForDate(period.end)
    const atRiskOpen = exposure.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.openAmount || 0)), 0)
    const reserveAmount = exposure.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.expectedWriteOffAmount || 0)), 0)
    const expectedCollectibleAmount = exposure.reduce((sum: number, invoice: any) => sum + Math.max(0, Number(invoice.expectedCollectibleAmount || 0)), 0)
    const trailingStart = monthStart(addMonths(period.end, -11))
    const trailingSales = invoiceSalesBetween(invoices, trailingStart, period.end)
    return {
      periodStart: period.start,
      periodEnd: period.end,
      atRiskOpen,
      expectedCollectibleAmount,
      reserveAmount,
      trailingSales,
      percentOfSales: trailingSales > 0.005 ? reserveAmount / trailingSales : null,
    }
  })
  const badDebtReserve = {
    asOfDate,
    recommendedMethod: 'aging_expected_loss',
    currentMonthSales,
    trailingSalesStart,
    trailingTwelveMonthSales,
    recommendedReserveAmount: currentExpectedWriteOff,
    recommendedPercentOfSales: currentReservePercentOfSales,
    recommendedCurrentMonthAccrual: currentReservePercentOfSales == null ? null : currentMonthSales * currentReservePercentOfSales,
    expectedCollectibleAmount: currentExpectedCollectible,
    atRiskOpenAmount: currentAtRiskOpen,
    methods: [
      {
        id: 'aging_expected_loss',
        label: 'Age-weighted expected loss',
        reserveAmount: currentExpectedWriteOff,
        percentOfSales: currentReservePercentOfSales,
        currentMonthAccrual: currentReservePercentOfSales == null ? null : currentMonthSales * currentReservePercentOfSales,
        explanation: 'Recommended. Each currently open at-risk invoice is mapped to its deepest aging threshold and multiplied by the historical non-recovery rate for that threshold.',
      },
      {
        id: 'cohort_loss_rate',
        label: 'Blended cohort loss rate',
        reserveAmount: cohortReserveAmount,
        percentOfSales: cohortPercentOfSales,
        currentMonthAccrual: cohortPercentOfSales == null ? null : currentMonthSales * cohortPercentOfSales,
        explanation: 'Applies one blended historical loss rate from all threshold cohorts to the current at-risk balance. This is simpler and less sensitive to the current age mix.',
      },
      {
        id: 'ninety_plus_simple',
        label: 'Simple 90+ loss rate',
        reserveAmount: simpleNinetyReserveAmount,
        percentOfSales: simpleNinetyPercentOfSales,
        currentMonthAccrual: simpleNinetyPercentOfSales == null ? null : currentMonthSales * simpleNinetyPercentOfSales,
        explanation: 'Applies the broad 90+ historical non-recovery rate to all current at-risk invoices. This is easiest to explain, but it ignores whether invoices are 90, 180, 270, or 360+ days late.',
      },
    ],
    trend: reserveTrend,
    methodology: {
      recommended: 'Age-weighted expected loss is the primary method because it uses the current receivable population and the company’s own observed recovery rates by aging threshold.',
      reserveBalance: 'Target allowance balance = sum(open invoice amount * historical non-recovery rate for its current aging threshold).',
      percentOfSales: 'Bad-debt reserve percent of sales = target allowance balance divided by trailing twelve-month invoice sales.',
      currentMonthAccrual: 'Illustrative current-month accrual = current-month invoice sales multiplied by the recommended reserve percent of sales. If an allowance balance already exists, the actual adjusting entry should true up to the target allowance balance.',
      trend: 'Monthly trend re-scores each month-end at-risk A/R using the current historical recovery table, so the line shows how the reserve signal has moved over time.',
    },
  }
  const customersById = new Map<string, any>()
  for (const invoice of invoices) {
    const openAmount = openOn(invoice, asOfDate)
    if (openAmount < 1) continue
    const daysPastDue = invoice.dueDate ? daysBetween(invoice.dueDate, asOfDate) : null
    const thresholdDays = thresholdForPastDueDays(daysPastDue)
    const id = invoice.customerId || invoice.customer
    const row = customersById.get(id) || {
      customerId: id,
      customer: invoice.customer,
      invoiceCount: 0,
      openAmount: 0,
      over90Amount: 0,
      over180Amount: 0,
      over270Amount: 0,
      over360Amount: 0,
      expectedCollectibleAmount: 0,
      expectedWriteOffAmount: 0,
      unknownBenchmarkAmount: 0,
      worstPastDueDays: 0,
    }
    row.invoiceCount += 1
    row.openAmount += openAmount
    row.worstPastDueDays = Math.max(row.worstPastDueDays, Number(daysPastDue || 0))
    if (Number(daysPastDue || 0) >= 90) row.over90Amount += openAmount
    if (Number(daysPastDue || 0) >= 180) row.over180Amount += openAmount
    if (Number(daysPastDue || 0) >= 270) row.over270Amount += openAmount
    if (Number(daysPastDue || 0) >= 360) row.over360Amount += openAmount
    if (thresholdDays != null) {
      const benchmarkRecoveryRate = thresholdByDays.get(thresholdDays)?.amountRecoveryRate ?? null
      if (benchmarkRecoveryRate == null) {
        row.unknownBenchmarkAmount += openAmount
      } else {
        const expectedCollectible = openAmount * benchmarkRecoveryRate
        row.expectedCollectibleAmount += expectedCollectible
        row.expectedWriteOffAmount += Math.max(0, openAmount - expectedCollectible)
      }
    }
    customersById.set(id, row)
  }
  const customers = Array.from(customersById.values())
    .filter((customer) => customer.over90Amount >= 1)
    .sort((a, b) => Number(b.expectedWriteOffAmount || b.over90Amount || 0) - Number(a.expectedWriteOffAmount || a.over90Amount || 0))
  return {
    asOfDate,
    thresholds,
    customers,
    currentInvoices,
    badDebtReserve,
    sampleInvoiceCount: benchmarkInvoices.length,
    leftCensoredInvoiceCount: skippedLeftCensored,
    missingDueInvoiceCount: skippedMissingDue,
    warnings,
    methodology: {
      cohortDefinition: 'An invoice enters a threshold cohort only if it still had at least $1 open on the date it crossed that many days past due.',
      recoveryRate: 'Dollars recovered after the threshold date divided by dollars open at the threshold date.',
      fullPaymentRate: 'Invoices fully collected after crossing the threshold divided by invoices in the threshold cohort.',
      currentExposure: 'Current open invoices are mapped to the deepest crossed threshold and benchmarked against historical dollar recovery after that threshold.',
      accountingUse: 'This is an empirical collectability signal for allowance/write-off discussion, not a GAAP policy by itself.',
    },
  }
}

const bucketReconciliationTolerance = (endingAr: number): number => (
  Math.max(100, Math.abs(endingAr) * 0.02)
)

const reconcileBucketsToEndingAr = (
  buckets: ArBucketAmounts,
  endingAr: number,
  knownReconcilingDifferenceAr = 0,
): ReconciledArBucketAmounts => {
  const sourceBucketTotalAr = buckets.currentAr + buckets.under90Ar + buckets.over90Ar
  const bucketReconciliationAr = endingAr - sourceBucketTotalAr
  const sourceExcessAr = Math.max(0, -bucketReconciliationAr)
  const knownDifferenceExplainsReconciliation = Math.abs(
    bucketReconciliationAr - knownReconcilingDifferenceAr,
  ) <= bucketReconciliationTolerance(endingAr)
  if (sourceExcessAr > bucketReconciliationTolerance(endingAr) && !knownDifferenceExplainsReconciliation) {
    const scale = sourceBucketTotalAr > 0.005 && endingAr > 0 ? Math.max(0, endingAr) / sourceBucketTotalAr : 0
    return {
      ...buckets,
      currentAr: buckets.currentAr * scale,
      under90Ar: buckets.under90Ar * scale,
      over90Ar: buckets.over90Ar * scale,
      over60Ar: buckets.over60Ar * scale,
      missingDueAr: buckets.missingDueAr * scale,
      sourceBucketTotalAr,
      agingBucketTotalAr: Math.max(0, endingAr),
      unclassifiedAr: 0,
      sourceExcessAr,
      bucketReconciliationAr,
      knownReconcilingDifferenceAr,
    }
  }
  const agingBucketTotalAr = sourceBucketTotalAr
  return {
    ...buckets,
    sourceBucketTotalAr,
    agingBucketTotalAr,
    unclassifiedAr: Math.max(0, bucketReconciliationAr),
    sourceExcessAr,
    bucketReconciliationAr,
    knownReconcilingDifferenceAr,
  }
}

const authoritativeBalanceValue = (
  balances: AuthoritativeEndingBalances | undefined,
  key: string,
): number | null => {
  if (!balances || !key) return null
  const value = balances instanceof Map ? balances.get(key) : balances[key]
  if (value == null) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const authoritativeBalanceForDate = (
  balances: AuthoritativeEndingBalances | undefined,
  date: string,
  options: { allowMonthFallback?: boolean } = {},
): number | null => {
  const key = dateKey(date)
  if (!key) return null
  const monthKey = key.slice(0, 7)
  const monthStart = `${monthKey}-01`
  const candidates = options.allowMonthFallback === false ? [key] : [key, monthKey, monthStart]
  for (const candidate of candidates) {
    const value = authoritativeBalanceValue(balances, candidate)
    if (value != null) return value
  }
  return null
}

const periodMetrics = (
  invoices: ArInvoice[],
  credits: ArUnappliedCredit[] = [],
  salesAdjustments: ArSalesAdjustment[] = [],
  start: string,
  end: string,
  ledgerEvents: ArLedgerEvent[] = [],
  agingSnapshotRows: ArSourceRow[] = [],
  controls: {
    authoritativeEndingAr?: number | null
    authoritativeOpeningAr?: number | null
    periodSales?: number | null
    salesWindowStart?: string | null
    reconcileBucketsToEndingAr?: boolean
  } = {},
) => {
  const openingNonInvoiceAr = nonInvoiceArBalanceOn(ledgerEvents, addDays(start, -1))
  const endingNonInvoiceAr = nonInvoiceArBalanceOn(ledgerEvents, end)
  const rawOpeningAr = invoiceBalanceOn(invoices, addDays(start, -1)) - creditBalanceOn(credits, addDays(start, -1)) + openingNonInvoiceAr
  const rawEndingAr = invoiceBalanceOn(invoices, end) - creditBalanceOn(credits, end) + endingNonInvoiceAr
  const activityOpeningAr = ledgerEvents.length ? ledgerBalanceOn(ledgerEvents, addDays(start, -1)) : rawOpeningAr
  const activityEndingAr = ledgerEvents.length ? ledgerBalanceOn(ledgerEvents, end) : rawEndingAr
  const snapshotBuckets = agingSnapshotBucketAmounts(agingSnapshotRows, end)
  const snapshotBucketTotalAr = bucketTotalAr(snapshotBuckets)
  const hasSnapshotBuckets = Math.abs(snapshotBucketTotalAr) > 0.005
  const hasFinancialEndingControl = controls.authoritativeEndingAr != null && Number.isFinite(Number(controls.authoritativeEndingAr))
  const hasFinancialOpeningControl = controls.authoritativeOpeningAr != null && Number.isFinite(Number(controls.authoritativeOpeningAr))
  const authoritativeEndingAr = hasFinancialEndingControl
    ? Number(controls.authoritativeEndingAr)
    : null
  const authoritativeOpeningAr = hasFinancialOpeningControl
    ? Number(controls.authoritativeOpeningAr)
    : null
  const endingArSource = hasFinancialEndingControl
    ? 'financial_statement'
    : 'invoice_rollforward'
  const controlledPeriodSales = controls.periodSales != null && Number.isFinite(Number(controls.periodSales)) && Math.abs(Number(controls.periodSales)) > 0.005
    ? Math.abs(Number(controls.periodSales))
    : null
  let endingAr = authoritativeEndingAr ?? rawEndingAr
  const endingAdjustment = authoritativeEndingAr == null ? 0 : authoritativeEndingAr - rawEndingAr
  const openingAdjustment = authoritativeOpeningAr == null ? endingAdjustment : authoritativeOpeningAr - rawOpeningAr
  const controlAdjusted = authoritativeEndingAr != null || authoritativeOpeningAr != null
  const knownReconcilingDifferenceAr = ledgerEvents.length ? ledgerOpeningDifferenceOn(ledgerEvents, end) : 0
  let day = start
  let dayCount = 0
  let balanceSum = 0
  let dsoBalanceSum = 0
  while (day <= end) {
    const dailyRawBalance = invoiceBalanceOn(invoices, day) - creditBalanceOn(credits, day) + nonInvoiceArBalanceOn(ledgerEvents, day)
    const dailyKnownDifference = ledgerEvents.length ? ledgerOpeningDifferenceOn(ledgerEvents, day) : 0
    const progress = dayCount <= 0 ? 0 : dayCount / Math.max(1, daysBetween(start, end))
    const dailyAdjustment = openingAdjustment + (endingAdjustment - openingAdjustment) * progress
    dayCount += 1
    balanceSum += dailyRawBalance + dailyAdjustment
    dsoBalanceSum += dailyRawBalance - dailyKnownDifference + dailyAdjustment
    day = addDays(day, 1)
  }
  const invoiceSales = netSalesBetween(invoices, salesAdjustments, start, end)
  const salesWindowStart = dateKey(controls.salesWindowStart)
  const trailingInvoiceSales = salesWindowStart
    ? netSalesBetween(invoices, salesAdjustments, salesWindowStart, end)
    : 0
  const trailingDayCount = salesWindowStart ? daysBetween(salesWindowStart, end) + 1 : dayCount
  const hasTrailingSales = salesWindowStart && trailingInvoiceSales > 0.005
  const dsoSales = controlledPeriodSales ?? (hasTrailingSales ? trailingInvoiceSales : invoiceSales)
  const dsoDayCount = controlledPeriodSales == null && hasTrailingSales ? trailingDayCount : dayCount
  const dsoSalesSource = controlledPeriodSales == null
    ? hasTrailingSales ? 'trailing_customer_invoice_sales' : 'invoice_origin_ar_sales'
    : 'financial_statement_sales'
  const invoiceEndingAr = invoiceBalanceOn(invoices, end)
  const unappliedCreditAr = creditBalanceOn(credits, end)
  const invoiceWeightedAverageAge = invoiceEndingAr > 0
    ? invoices.reduce((sum, invoice) => sum + openOn(invoice, end) * daysBetween(invoice.invoiceDate, end), 0) / invoiceEndingAr
    : null
  const invoiceBuckets = arBucketAmounts(invoices, credits, end)
  const rollforwardBuckets: ArBucketAmounts = {
    ...invoiceBuckets,
    currentAr: invoiceBuckets.currentAr + endingNonInvoiceAr,
    source: 'invoice_rollforward',
  }
  const rollforwardBucketTotalAr = bucketTotalAr(rollforwardBuckets)
  if (controls.reconcileBucketsToEndingAr === false && authoritativeEndingAr == null && Math.abs(rollforwardBucketTotalAr) > 0.005) {
    endingAr = rollforwardBucketTotalAr
  }
  const snapshotReconciled = hasSnapshotBuckets
    ? reconcileBucketsToEndingAr(snapshotBuckets, endingAr, knownReconcilingDifferenceAr)
    : null
  const buckets = reconcileBucketsToEndingAr(rollforwardBuckets, endingAr, knownReconcilingDifferenceAr)
  const bucketSplitReliable = buckets.source !== 'unreconciled_activity' && buckets.source !== 'unreconciled_aging_snapshot'
  const overdueAr = bucketSplitReliable ? buckets.under90Ar + buckets.over90Ar : null
  const over60Ar = bucketSplitReliable ? buckets.over60Ar : null
  return {
    period: start,
    periodStart: start,
    periodEnd: end,
    averageAr: dayCount ? balanceSum / dayCount : 0,
    endingAr,
    activityEndingAr,
    invoiceEndingAr,
    unappliedCreditAr,
    authoritativeEndingAr,
    endingArSource,
    reconciliationAdjustment: endingAdjustment,
    controlAdjusted,
    ...buckets,
    invoiceSales,
    dsoSales,
    dsoSalesSource,
    averageDso: dsoSales ? dsoBalanceSum / dsoSales : null,
    dso: dsoSales ? (Math.max(0, endingAr - knownReconcilingDifferenceAr) * dsoDayCount) / dsoSales : null,
    weightedAverageAge: bucketSplitReliable ? (buckets.weightedAverageAge ?? invoiceWeightedAverageAge) : null,
    overdueAr,
    over60Ar,
    agingSource: buckets.source,
    reconciliationDiagnostics: {
      financialStatementEndingAr: hasFinancialEndingControl ? endingAr : null,
      controlledEndingAr: endingAr,
      activityOpeningAr,
      activityEndingAr,
      invoiceEndingAr,
      nonInvoiceAr: endingNonInvoiceAr,
      agingSnapshotBucketTotalAr: snapshotBucketTotalAr,
      agingSnapshotBucketReconciliationAr: snapshotReconciled?.bucketReconciliationAr ?? null,
      agingSnapshotVarianceToRollforwardAr: snapshotBucketTotalAr - rawEndingAr,
      sourceBucketTotalAr: buckets.sourceBucketTotalAr,
      agingBucketTotalAr: buckets.agingBucketTotalAr,
      knownReconcilingDifferenceAr,
      endingControlVarianceAr: endingAr - rawEndingAr,
      invoiceVarianceToFinancialAr: endingAr - invoiceEndingAr,
      bucketVarianceToFinancialAr: endingAr - buckets.sourceBucketTotalAr,
    },
  }
}

const invoiceDrilldownForPeriod = (invoices: ArInvoice[], start: string, end: string) => invoices
  .map((invoice) => ({
    invoiceKey: invoice.key,
    periodStart: start,
    periodEnd: end,
    customerId: invoice.customerId,
    customer: invoice.customer,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate || null,
    dueDateSource: invoice.dueDateSource,
    originalAmount: invoice.originalAmount,
    openAmount: openOn(invoice, end),
    daysOpen: daysBetween(invoice.invoiceDate, end),
    daysPastDue: invoice.dueDate ? daysBetween(invoice.dueDate, end) : null,
    allocationMethod: invoice.allocationMethod,
  }))
  .filter((invoice) => Number(invoice.openAmount || 0) > 0.005)
  .sort((a, b) => Number(b.openAmount || 0) - Number(a.openAmount || 0))

type QuickBooksArTrendAnalysisArgs = {
  rows: ArSourceRow[]
  asOfDate?: string
  periodCount?: number
  periodGrain?: ArPeriodGrain | string
  topCustomerLimit?: number
  authoritativeEndingBalances?: AuthoritativeEndingBalances
  periodSales?: PeriodSalesControls
  excludedCustomerKeys?: string[]
}

export function buildQuickBooksArCollectabilityAnalysis(args: QuickBooksArTrendAnalysisArgs): QuickBooksArTrendAnalysis | null {
  const periodGrain = normalizePeriodGrain(args.periodGrain)
  const periods = periodsForGrain(args.asOfDate || new Date().toISOString().slice(0, 10), args.periodCount || 13, periodGrain)
  const latest = periods[periods.length - 1]
  if (!latest) return null
  const rows = uniqueArSourceRows(args.rows)
  const exclusionKeys = new Set((args.excludedCustomerKeys || []).map(customerExclusionKey).filter(Boolean))
  const excludedEntityCustomers = excludedLedgerEntityCustomers(rows, exclusionKeys)
  const { invoices: reconstructedInvoices, warnings } = reconstructInvoices(rows)
  const invoices = reconstructedInvoices.filter((invoice) => !shouldExcludeCustomer(invoice.customerId, invoice.customer, exclusionKeys))
  if (!invoices.length) return null
  const collectability = buildCollectabilityAnalysis(invoices, latest.end)
  return {
    asOfDate: latest.end,
    periodGrain,
    topCustomerLimit: Math.max(1, Math.min(20, Math.floor(Number(args.topCustomerLimit) || 5))),
    excludedCustomers: excludedCustomerSummaries(reconstructedInvoices, [], latest.end, exclusionKeys, excludedEntityCustomers),
    periods: [],
    customerPeriodTrends: {},
    customerTrends: [],
    topCustomerConcerns: [],
    invoiceDrilldowns: [],
    periodInvoiceDrilldowns: [],
    collectability,
    warnings,
    methodology: {
      periodGrain,
      cohortDefinition: collectability.methodology.cohortDefinition,
      recoveryRate: collectability.methodology.recoveryRate,
      fullPaymentRate: collectability.methodology.fullPaymentRate,
      currentExposure: collectability.methodology.currentExposure,
      accountingUse: collectability.methodology.accountingUse,
    },
  }
}

export function buildQuickBooksArTrendAnalysis(args: QuickBooksArTrendAnalysisArgs): QuickBooksArTrendAnalysis | null {
  const periodGrain = normalizePeriodGrain(args.periodGrain)
  const periods = periodsForGrain(args.asOfDate || new Date().toISOString().slice(0, 10), args.periodCount || 13, periodGrain)
  const allowMonthlyControlFallback = periodGrain === 'month' || periodGrain === 'quarter' || periodGrain === 'year'
  const rows = uniqueArSourceRows(args.rows)
  const exclusionKeys = new Set((args.excludedCustomerKeys || []).map(customerExclusionKey).filter(Boolean))
  const useAuthoritativeControls = exclusionKeys.size === 0
  const excludedEntityCustomers = excludedLedgerEntityCustomers(rows, exclusionKeys)
  const agingSnapshotRows = rows.filter((row) => !shouldExcludeCustomer(row.customer_id, row.customer, exclusionKeys)).filter(isAgingBucketSourceRow)
  const ledgerEventsAll = ledgerEventsFromRows(rows)
  const ledgerEvents = ledgerEventsAll.filter((event) => !shouldExcludeLedgerEvent(event, exclusionKeys, excludedEntityCustomers))
  const { invoices: reconstructedInvoices, credits: reconstructedCredits, warnings } = reconstructInvoices(rows)
  const invoices = reconstructedInvoices.filter((invoice) => !shouldExcludeCustomer(invoice.customerId, invoice.customer, exclusionKeys))
  const credits = reconstructedCredits.filter((credit) => !shouldExcludeCustomer(credit.customerId, credit.customer, exclusionKeys))
  const salesAdjustments = salesAdjustmentRows(rows).filter((event) => !shouldExcludeCustomer(event.customerId, event.customer, exclusionKeys))
  if (!ledgerEvents.length || !periods.length) return null
  if (!invoices.length) {
    warnings.push('Invoice-level A/R aging is unavailable because no invoice-origin receivable activity was found in the synced A/R detail.')
  }
  const latest = periods[periods.length - 1]
  const excludedCustomers = excludedCustomerSummaries(reconstructedInvoices, ledgerEventsAll, latest.end, exclusionKeys, excludedEntityCustomers)
  const periodRows = periods.map((period) => periodMetrics(invoices, credits, salesAdjustments, period.start, period.end, ledgerEvents, agingSnapshotRows, {
    authoritativeEndingAr: useAuthoritativeControls ? authoritativeBalanceForDate(args.authoritativeEndingBalances, period.end, { allowMonthFallback: allowMonthlyControlFallback }) : null,
    authoritativeOpeningAr: useAuthoritativeControls ? authoritativeBalanceForDate(args.authoritativeEndingBalances, addDays(period.start, -1), { allowMonthFallback: allowMonthlyControlFallback }) : null,
    periodSales: useAuthoritativeControls ? authoritativeBalanceForDate(args.periodSales, period.end, { allowMonthFallback: allowMonthlyControlFallback }) : null,
  }))
  if (periodRows.some((period) => Boolean(period.controlAdjusted))) {
    warnings.push('Overall A/R trend ending balances are anchored to financial statement controls when available, otherwise exact-date A/R aging snapshots. Customer and invoice aging detail uses the available invoice/payment A/R detail, with opening aging detail only as a seed for invoices whose pre-history is missing.')
  }
  if (!useAuthoritativeControls) {
    warnings.push('Customer exclusions are active, so A/R trend metrics use the filtered invoice/payment rollforward instead of company-level financial statement controls that still include excluded customers.')
  }
  const totalOpen = Number(periodRows[periodRows.length - 1]?.endingAr ?? ledgerBalanceOn(ledgerEvents, latest.end))
  const byCustomer = new Map<string, ArInvoice[]>()
  invoices.forEach((invoice) => byCustomer.set(invoice.customerId, [...(byCustomer.get(invoice.customerId) || []), invoice]))
  const creditsByCustomer = new Map<string, ArUnappliedCredit[]>()
  credits.forEach((credit) => creditsByCustomer.set(credit.customerId, [...(creditsByCustomer.get(credit.customerId) || []), credit]))
  const salesAdjustmentsByCustomer = new Map<string, ArSalesAdjustment[]>()
  salesAdjustments.forEach((event) => salesAdjustmentsByCustomer.set(event.customerId, [...(salesAdjustmentsByCustomer.get(event.customerId) || []), event]))
  const ledgerByCustomer = new Map<string, ArLedgerEvent[]>()
  ledgerEvents.forEach((event) => ledgerByCustomer.set(event.customerId, [...(ledgerByCustomer.get(event.customerId) || []), event]))
  const agingSnapshotsByCustomer = new Map<string, ArSourceRow[]>()
  agingSnapshotRows.forEach((row) => {
    const customer = cleanText(row.customer) || 'Unassigned customer'
    const customerId = cleanText(row.customer_id) || customer
    agingSnapshotsByCustomer.set(customerId, [...(agingSnapshotsByCustomer.get(customerId) || []), row])
  })
  const customerPeriodTrends: NonNullable<QuickBooksArTrendAnalysis['customerPeriodTrends']> = {}
  const customerIds = new Set([...byCustomer.keys(), ...creditsByCustomer.keys(), ...salesAdjustmentsByCustomer.keys(), ...ledgerByCustomer.keys()])
  const customerTrends = Array.from(customerIds).map((customerId) => {
    const customerInvoices = byCustomer.get(customerId) || []
    const customerCredits = creditsByCustomer.get(customerId) || []
    const customerSalesAdjustments = salesAdjustmentsByCustomer.get(customerId) || []
    const customerLedgerEvents = ledgerByCustomer.get(customerId) || []
    const customerAgingSnapshots = agingSnapshotsByCustomer.get(customerId) || []
    const fallbackCustomer = customerLedgerEvents[0]?.customer || customerInvoices[0]?.customer || customerCredits[0]?.customer || customerSalesAdjustments[0]?.customer || 'Unassigned customer'
    const customerSalesWindowStart = periods[0]?.start || latest.start
    customerPeriodTrends[customerId] = periods.map((period) => periodMetrics(customerInvoices, customerCredits, customerSalesAdjustments, period.start, period.end, customerLedgerEvents, customerAgingSnapshots, {
      salesWindowStart: customerSalesWindowStart,
      reconcileBucketsToEndingAr: false,
    }))
    const current = periodMetrics(customerInvoices, customerCredits, customerSalesAdjustments, latest.start, latest.end, customerLedgerEvents, customerAgingSnapshots, {
      salesWindowStart: customerSalesWindowStart,
      reconcileBucketsToEndingAr: false,
    })
    const previousPeriod = periods.length > 1 ? periods[periods.length - 2] : null
    const previous = previousPeriod ? periodMetrics(customerInvoices, customerCredits, customerSalesAdjustments, previousPeriod.start, previousPeriod.end, customerLedgerEvents, customerAgingSnapshots, {
      salesWindowStart: customerSalesWindowStart,
      reconcileBucketsToEndingAr: false,
    }) : current
    const worsening = Math.max(0, Number(current.weightedAverageAge || 0) - Number(previous.weightedAverageAge || current.weightedAverageAge || 0))
    const overdueAr = Number(current.overdueAr || 0)
    const over60Ar = Number(current.over60Ar || 0)
    const score = overdueAr + over60Ar * 1.5 + current.endingAr * Math.min(2, worsening / 30) + current.endingAr * (totalOpen ? current.endingAr / totalOpen : 0)
    return {
      customerId,
      customer: fallbackCustomer,
      endingAr: current.endingAr,
      overdueAr: current.overdueAr,
      over60Ar: current.over60Ar,
      over90Ar: current.over90Ar,
      dso: current.dso,
      weightedAverageAge: current.weightedAverageAge,
      riskScore: score,
      openInvoiceCount: customerInvoices.filter((invoice) => openOn(invoice, latest.end) > 0.005).length,
    }
  })
  const topCustomerLimit = Math.max(1, Math.min(20, Math.floor(Number(args.topCustomerLimit) || 5)))
  const topCustomerConcerns = [...customerTrends].sort((a, b) => Number(b.riskScore || 0) - Number(a.riskScore || 0)).slice(0, topCustomerLimit)
  const invoiceDrilldowns = invoiceDrilldownForPeriod(invoices, latest.start, latest.end)
  const periodInvoiceDrilldowns = periods
    .flatMap((period) => invoiceDrilldownForPeriod(invoices, period.start, period.end))
  const collectability = buildCollectabilityAnalysis(invoices, latest.end)
  return {
    asOfDate: latest.end,
    periodGrain,
    topCustomerLimit,
    excludedCustomers,
    periods: periodRows,
    customerPeriodTrends,
    customerTrends: customerTrends.sort((a, b) => Number(b.endingAr || 0) - Number(a.endingAr || 0)),
    topCustomerConcerns,
    invoiceDrilldowns,
    periodInvoiceDrilldowns,
    collectability,
    warnings,
    methodology: {
      periodGrain,
      salesBasis: 'invoice_ar',
      dsoFormula: 'ending_controlled_ar_excluding_opening_difference * period_days / financial_statement_sales_when_available',
      weightedAgeFormula: 'sum(open_invoice_amount * days_since_invoice_date) / ending_open_ar',
      paymentAllocation: 'direct invoice/payment links reduce invoice balances when the invoice exists; payments dated before their linked invoice, or not resolved to an invoice, remain as negative unapplied A/R credit balances bucketed by the same due-date aging method QuickBooks uses',
      overallArControl: 'trial_balance_ending_balance_when_available',
      bucketReconciliation: 'opening aging detail is rolled forward with invoice/payment A/R history; any opening trial-balance-to-aging-detail difference is carried as a separate discrepancy row and excluded from DSO, overdue, and weighted-age calculations',
    },
  }
}

const arCustomerMatchesExclusion = (customerId: any, customer: any, exclusions: Set<string>): boolean => (
  exclusions.has(customerExclusionKey(customerId)) || exclusions.has(customerExclusionKey(customer))
)

const arPeriodByStart = (
  periods: QuickBooksArTrendAnalysis['periods'] | undefined,
): Map<string, QuickBooksArTrendAnalysis['periods'][number]> => new Map(
  (periods || []).map((period) => [String(period.periodStart || period.period || ''), period]),
)

export function applyQuickBooksArTrendExclusions(
  analysis: QuickBooksArTrendAnalysis | null,
  excludedCustomerKeys: string[] = [],
  rows: ArSourceRow[] = [],
): QuickBooksArTrendAnalysis | null {
  if (!analysis) return analysis
  const exclusions = new Set(excludedCustomerKeys.map(customerExclusionKey).filter(Boolean))
  if (!exclusions.size) return analysis
  const customerTrends = Array.isArray(analysis.customerTrends) ? analysis.customerTrends : []
  const excludedCustomerIds = new Set<string>()
  const excludedCustomers = customerTrends.filter((customer) => {
    const matched = arCustomerMatchesExclusion(customer.customerId, customer.customer, exclusions)
    if (matched) excludedCustomerIds.add(String(customer.customerId || customer.customer || ''))
    return matched
  })
  if (!excludedCustomerIds.size) {
    for (const key of exclusions) excludedCustomerIds.add(key)
  }
  const excludedSnapshotBucketsByPeriod = new Map<string, { endingAr: number; currentAr: number; under90Ar: number; over90Ar: number; over60Ar: number }>()
  if (rows.length) {
    const periodEnds = new Set((analysis.periods || []).map((period) => String(period.periodEnd || '')))
    for (const row of rows) {
      const periodEnd = dateKey(row.txn_date)
      if (!periodEnds.has(periodEnd) || !isAgingBucketSourceRow(row) || !shouldExcludeCustomer(row.customer_id, row.customer, exclusions)) continue
      const amount = amountForRow(row)
      if (Math.abs(amount) <= 0.005) continue
      const bucket = excludedSnapshotBucketsByPeriod.get(periodEnd) || { endingAr: 0, currentAr: 0, under90Ar: 0, over90Ar: 0, over60Ar: 0 }
      bucket.endingAr += amount
      const dueDate = dateKey(row.invoice_due_date)
      const invoiceDate = dateKey(row.invoice_date) || periodEnd
      if (dueDate) {
        addAmountToDueDateBucket(amount, dueDate, periodEnd, bucket)
      } else if (daysBetween(invoiceDate, periodEnd) <= 90) {
        bucket.under90Ar += amount
      } else {
        bucket.over90Ar += amount
        bucket.over60Ar += amount
      }
      excludedSnapshotBucketsByPeriod.set(periodEnd, bucket)
    }
  }
  const isExcludedCustomer = (customerId: any, customer: any): boolean => (
    excludedCustomerIds.has(String(customerId || customer || ''))
    || arCustomerMatchesExclusion(customerId, customer, exclusions)
  )
  const excludedPeriodMaps = Array.from(excludedCustomerIds)
    .map((customerId) => arPeriodByStart(analysis.customerPeriodTrends?.[customerId]))
  const adjustedPeriods = (analysis.periods || []).map((period) => {
    let averageAr = Number(period.averageAr || 0)
    let endingAr = Number(period.endingAr || 0)
    let activityEndingAr = Number(period.activityEndingAr || 0)
    let invoiceEndingAr = Number(period.invoiceEndingAr || 0)
    let unappliedCreditAr = Number((period as any).unappliedCreditAr || 0)
    let currentAr = Number(period.currentAr || 0)
    let under90Ar = Number(period.under90Ar || 0)
    let over90Ar = Number(period.over90Ar || 0)
    let over60Ar = Number(period.over60Ar || 0)
    let missingDueAr = Number(period.missingDueAr || 0)
    let invoiceSales = Number(period.invoiceSales || 0)
    let dsoSales = String(period.dsoSalesSource || '') === 'financial_statement_sales'
      ? Number(period.invoiceSales || 0)
      : Number(period.dsoSales || 0)
    let weightedNumerator = Number(period.weightedAverageAge || 0) * Math.max(0, Number(period.agingBucketTotalAr || period.sourceBucketTotalAr || 0))
    const periodKey = String(period.periodStart || period.period || '')
    const excludedSnapshot = excludedSnapshotBucketsByPeriod.get(String(period.periodEnd || ''))
    if (excludedSnapshot) {
      endingAr -= excludedSnapshot.endingAr
      activityEndingAr -= excludedSnapshot.endingAr
      invoiceEndingAr -= excludedSnapshot.endingAr
      currentAr -= excludedSnapshot.currentAr
      under90Ar -= excludedSnapshot.under90Ar
      over90Ar -= excludedSnapshot.over90Ar
      over60Ar -= excludedSnapshot.over60Ar
    }
    for (const excludedPeriods of excludedPeriodMaps) {
      const excluded = excludedPeriods.get(periodKey)
      if (!excluded) continue
      const excludedBucketTotal = Math.max(0, Number(excluded.agingBucketTotalAr || excluded.sourceBucketTotalAr || 0))
      averageAr -= Number(excluded.averageAr || 0)
      if (!excludedSnapshot) {
        endingAr -= Number(excluded.endingAr || 0)
        activityEndingAr -= Number(excluded.activityEndingAr || 0)
        invoiceEndingAr -= Number(excluded.invoiceEndingAr || 0)
        currentAr -= Number(excluded.currentAr || 0)
        under90Ar -= Number(excluded.under90Ar || 0)
        over90Ar -= Number(excluded.over90Ar || 0)
        over60Ar -= Number(excluded.over60Ar || 0)
      }
      unappliedCreditAr -= Number((excluded as any).unappliedCreditAr || 0)
      missingDueAr -= Number(excluded.missingDueAr || 0)
      invoiceSales -= Number(excluded.invoiceSales || 0)
      dsoSales -= Number(excluded.invoiceSales || excluded.dsoSales || 0)
      weightedNumerator -= Number(excluded.weightedAverageAge || 0) * excludedBucketTotal
    }
    const sourceBucketTotalAr = currentAr + under90Ar + over90Ar
    const bucketReconciliationAr = endingAr - sourceBucketTotalAr
    const dayCount = daysBetween(String(period.periodStart || period.period), String(period.periodEnd || period.period)) + 1
    const weightedAverageAge = sourceBucketTotalAr > 0.005 ? weightedNumerator / sourceBucketTotalAr : null
    const dso = dsoSales > 0.005 ? (Math.max(0, endingAr) * dayCount) / dsoSales : null
    const averageDso = dsoSales > 0.005 ? (Math.max(0, averageAr) * dayCount) / dsoSales : null
    return {
      ...period,
      averageAr,
      endingAr,
      activityEndingAr,
      invoiceEndingAr,
      unappliedCreditAr,
      authoritativeEndingAr: null,
      endingArSource: 'invoice_rollforward',
      reconciliationAdjustment: 0,
      controlAdjusted: false,
      currentAr,
      under90Ar,
      over90Ar,
      over60Ar,
      missingDueAr,
      weightedAverageAge,
      source: 'invoice_rollforward',
      sourceBucketTotalAr,
      agingBucketTotalAr: sourceBucketTotalAr,
      unclassifiedAr: 0,
      sourceExcessAr: Math.max(0, -bucketReconciliationAr),
      bucketReconciliationAr,
      invoiceSales,
      dsoSales,
      dsoSalesSource: 'invoice_origin_ar_sales',
      averageDso,
      dso,
      overdueAr: under90Ar + over90Ar,
      agingSource: 'invoice_rollforward',
      reconciliationDiagnostics: {
        ...(period.reconciliationDiagnostics || {}),
        financialStatementEndingAr: null,
        controlledEndingAr: endingAr,
        sourceBucketTotalAr,
        agingBucketTotalAr: sourceBucketTotalAr,
        endingControlVarianceAr: 0,
        bucketVarianceToFinancialAr: bucketReconciliationAr,
      },
    }
  })
  const filteredCustomerTrends = customerTrends.filter((customer) => !isExcludedCustomer(customer.customerId, customer.customer))
  const filteredCustomerPeriodTrends = Object.fromEntries(
    Object.entries(analysis.customerPeriodTrends || {})
      .filter(([customerId]) => !isExcludedCustomer(customerId, customerId)),
  )
  const invoiceCountByCustomer = new Map<string, number>()
  for (const invoice of analysis.invoiceDrilldowns || []) {
    if (!isExcludedCustomer(invoice.customerId, invoice.customer)) continue
    const key = String(invoice.customerId || invoice.customer || '')
    invoiceCountByCustomer.set(key, (invoiceCountByCustomer.get(key) || 0) + 1)
  }
  const latestEnd = adjustedPeriods[adjustedPeriods.length - 1]?.periodEnd || analysis.asOfDate || ''
  const exclusionSummaries = excludedCustomers.map((customer) => {
    const trend = analysis.customerPeriodTrends?.[String(customer.customerId || customer.customer || '')]
    const latest = trend?.find((period) => period.periodEnd === latestEnd) || trend?.[trend.length - 1]
    const key = String(customer.customerId || customer.customer || '')
    return {
      customerId: customer.customerId,
      customer: customer.customer,
      endingAr: Number(latest?.endingAr || customer.endingAr || 0),
      invoiceCount: invoiceCountByCustomer.get(key) || customer.openInvoiceCount || 0,
    }
  })
  return {
    ...analysis,
    excludedCustomers: exclusionSummaries,
    periods: adjustedPeriods,
    customerPeriodTrends: filteredCustomerPeriodTrends,
    customerTrends: filteredCustomerTrends.sort((a, b) => Number(b.endingAr || 0) - Number(a.endingAr || 0)),
    topCustomerConcerns: (analysis.topCustomerConcerns || []).filter((customer) => !isExcludedCustomer(customer.customerId, customer.customer)),
    invoiceDrilldowns: (analysis.invoiceDrilldowns || []).filter((invoice) => !isExcludedCustomer(invoice.customerId, invoice.customer)),
    periodInvoiceDrilldowns: (analysis.periodInvoiceDrilldowns || []).filter((invoice) => !isExcludedCustomer(invoice.customerId, invoice.customer)),
    warnings: [
      ...(analysis.warnings || []),
      'Customer exclusions are active, so A/R trend metrics are derived from the cached rollforward with excluded customer activity removed.',
    ],
  }
}
